/**
 * Aviso para instalar o MetaEpi no celular (PWA). Só aparece em aparelho de toque, quando o
 * sistema ainda não está instalado e a pessoa não dispensou o aviso.
 *  - Android: botão "Instalar", que abre a instalação do próprio celular.
 *  - iPhone: o Safari não permite botão; o aviso explica o caminho pelo menu Compartilhar.
 */
import { Share, Smartphone, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Botao } from '../components/ui';
import { cn } from '../lib/cn';
import {
  avisoDispensado,
  dispensarAviso,
  ehCelular,
  ehIphone,
  instalar,
  ouvirInstalacao,
  podeInstalarDireto,
  rodandoComoApp,
} from '../lib/instalacao';

function situacao() {
  if (rodandoComoApp() || avisoDispensado() || !ehCelular()) return null;
  // No iPhone o Safari nunca oferece a instalação direta: vale sempre a explicação
  if (ehIphone()) return 'iphone' as const;
  if (podeInstalarDireto()) return 'android' as const;
  return null;
}

export function AvisoInstalacao({ className }: { className?: string }) {
  const [modo, setModo] = useState(situacao);
  useEffect(() => ouvirInstalacao(() => setModo(situacao())), []);
  if (!modo) return null;

  const fechar = () => {
    dispensarAviso();
    setModo(null);
  };

  return (
    <section
      aria-label="Instalar o MetaEpi no celular"
      className={cn(
        'mx-auto flex w-full max-w-xl items-start gap-3 rounded-xl border border-borda bg-superficie p-4 text-sm text-texto shadow-sm',
        className,
      )}
    >
      <Smartphone className="mt-0.5 size-5 shrink-0 text-primaria" aria-hidden="true" />
      <div className="flex flex-1 flex-col gap-2">
        <p className="font-semibold">Instale o MetaEpi no seu celular</p>
        {modo === 'android' ? (
          <p className="text-texto-suave">
            Ele fica na tela inicial e abre em tela cheia, como um aplicativo. Nenhum dado de
            paciente fica guardado no aparelho.
          </p>
        ) : (
          <p className="text-texto-suave">
            No Safari, toque em{' '}
            <Share className="inline size-4 align-text-bottom" aria-label="Compartilhar" />{' '}
            <strong>Compartilhar</strong> e depois em <strong>Adicionar à Tela de Início</strong>.
            Nenhum dado de paciente fica guardado no aparelho.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {modo === 'android' && (
            <Botao
              tamanho="pequeno"
              onClick={() => {
                void instalar().then(() => setModo(situacao()));
              }}
            >
              Instalar
            </Botao>
          )}
          <Botao variante="fantasma" tamanho="pequeno" onClick={fechar}>
            Agora não
          </Botao>
        </div>
      </div>
      <button
        type="button"
        onClick={fechar}
        className="rounded p-1 text-texto-suave hover:bg-superficie-2"
        aria-label="Fechar o aviso de instalação"
      >
        <X className="size-4" aria-hidden="true" />
      </button>
    </section>
  );
}
